<!-- ABOUTME: Vue component for displaying email template details with tabbed language variants -->
<!-- ABOUTME: Shows template preview matching actual sent emails, uses Tailwind CSS exclusively -->
<template>
  <div class="max-w-6xl mx-auto px-4 py-6">
    <!-- Compact Header -->
    <div class="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
      <div class="flex items-center gap-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ template.template_key }}</h1>
        <span v-if="template.title" class="text-gray-500 text-sm">{{ template.title }}</span>
        <span :class="statusBadgeClass">{{ statusText }}</span>
      </div>
      <div class="flex gap-3">
        <button @click="duplicateTemplate" class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors">
          <Copy :size="16" class="mr-2" />
          Duplicate
        </button>
        <a :href="editUrl" class="inline-flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-md transition-colors">
          <Pencil :size="16" class="mr-2" />
          Edit
        </a>
        <a :href="indexUrl" class="inline-flex items-center px-4 py-2 bg-primary-color hover:bg-teal-600 text-white text-sm font-medium rounded-md transition-colors">
          Back to List
        </a>
      </div>
    </div>

    <!-- Compact Details Grid -->
    <div class="bg-white border border-gray-200 rounded-lg p-4 mb-5">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="flex items-baseline gap-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Created:</span>
          <span class="text-sm text-gray-900 font-medium">{{ formatDate(template.created_at) }}</span>
        </div>
        <div class="flex items-baseline gap-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Languages:</span>
          <span class="text-sm font-semibold text-primary-color">
            <span v-for="(variant, index) in template.variants" :key="variant.locale">
              {{ variant.locale.toUpperCase() }}<span v-if="index < template.variants.length - 1">, </span>
            </span>
          </span>
        </div>
        <div v-if="automationRulesCount > 0" class="flex items-baseline gap-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Used by:</span>
          <span class="text-sm font-semibold text-blue-600">{{ automationRulesCount }} automation rule(s)</span>
        </div>
      </div>
    </div>

    <!-- Email Preview with Tabs -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="bg-gray-50 border-b border-gray-200 p-4">
        <h2 class="flex items-center text-lg font-semibold text-gray-900 mb-4">
          <FileText :size="20" class="mr-2 text-primary-color" />
          Template Preview
        </h2>
        <div class="flex gap-2 flex-wrap">
          <button
            v-for="variant in template.variants"
            :key="variant.locale"
            @click="activeTab = variant.locale"
            :class="[
              'px-5 py-2 text-sm font-medium rounded-md transition-colors',
              activeTab === variant.locale
                ? 'bg-primary-color text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            ]"
          >
            {{ getLanguageName(variant.locale) }}
          </button>
        </div>
      </div>

      <div class="p-6">
        <div v-for="variant in template.variants"
             :key="variant.locale"
             v-show="activeTab === variant.locale"
             class="animate-fade-in">

          <!-- Subject -->
          <div class="mb-6">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Subject</div>
            <div class="text-base font-semibold text-gray-900 py-2">{{ renderSubject(variant) }}</div>
          </div>

          <!-- Email Body - 1:1 with actual email (white background, no gray) -->
          <div class="mb-2">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Email Body</div>
          </div>
          <div class="email-body-preview" v-html="renderBody(variant)"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Copy, Pencil, FileText } from 'lucide-vue-next'
import axios from 'axios'

const props = defineProps({
  template: { type: Object, required: true },
  indexUrl: { type: String, required: true },
  editUrl: { type: String, required: true },
  duplicateUrl: { type: String, required: true },
  automationRulesCount: { type: Number, default: 0 }
})

const activeTab = ref(props.template.variants[0]?.locale || 'en')

const sampleVariables = {
  first_name: 'John',
  last_name: 'Doe',
  email: 'john.doe@example.com',
  network_url: 'https://unlisters.com/network',
  projects_url: 'https://unlisters.com/projects',
  wants_url: 'https://unlisters.com/wants',
  login_url: 'https://unlisters.com/login'
}

const languageNames = {
  en: 'English',
  sk: 'Slovak',
  cs: 'Czech',
  hu: 'Hungarian'
}

const statusText = computed(() => {
  return props.template.status.charAt(0).toUpperCase() + props.template.status.slice(1)
})

const statusBadgeClass = computed(() => {
  const baseClasses = 'inline-block px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wide'
  const statusClasses = {
    draft: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    archived: 'bg-gray-100 text-gray-600'
  }
  return `${baseClasses} ${statusClasses[props.template.status] || 'bg-gray-100 text-gray-600'}`
})

function getLanguageName(locale) {
  return languageNames[locale] || locale.toUpperCase()
}

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function renderSubject(variant) {
  let subject = variant.subject
  Object.keys(sampleVariables).forEach(key => {
    const placeholder = `{{${key}}}`
    subject = subject.replace(new RegExp(placeholder, 'g'), sampleVariables[key])
  })
  return subject
}

function renderBody(variant) {
  let body = variant.body

  Object.keys(sampleVariables).forEach(key => {
    const placeholder = `{{${key}}}`
    body = body.replace(new RegExp(placeholder, 'g'), sampleVariables[key])
  })

  body = body.replace(/\{\{button:(primary|secondary|success|danger):(.+?):([^:}]+)\}\}/g, (match, style, url, text) => {
    return generateEmailButton(text, url, style)
  })

  body = body.replace(/\{\{link:(.+?):([^:}]+)\}\}/g, (match, url, text) => {
    return generateEmailLink(text, url)
  })

  return body
}

function generateEmailButton(text, url, style = 'primary') {
  const colors = {
    primary: { bg: '#17B7B7', text: '#ffffff' },
    secondary: { bg: '#2271B1', text: '#ffffff' },
    success: { bg: '#10b981', text: '#ffffff' },
    danger: { bg: '#ef4444', text: '#ffffff' }
  }

  const color = colors[style] || colors.primary

  return `
    <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="border-collapse: separate; width: auto; margin: 20px 0;">
      <tr>
        <td style="border-radius: 6px; background-color: ${color.bg}; text-align: center;">
          <a href="${url}" target="_blank" style="display: inline-block; padding: 12px 24px; font-family: sans-serif; font-size: 14px; font-weight: 600; color: ${color.text}; text-decoration: none; border-radius: 6px;">${text}</a>
        </td>
      </tr>
    </table>
  `
}

function generateEmailLink(text, url) {
  return `<a href="${url}" style="color: #17B7B7; text-decoration: none; font-weight: 500;">${text}</a>`
}

async function duplicateTemplate() {
  if (!confirm('Create a new draft template with the same content?')) return

  try {
    const response = await axios.post(props.duplicateUrl, {}, {
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'Accept': 'application/json'
      }
    })

    if (response.data.success) {
      window.location.href = response.data.redirect_url
    } else {
      alert('Failed to duplicate template')
    }
  } catch (error) {
    console.error('Failed to duplicate template:', error)
    alert('Error duplicating template')
  }
}

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : ''
}
</script>

<style scoped>
/* Minimal scoped styles - only for 1:1 email preview rendering */

/* Fade animation for tab switching */
.animate-fade-in {
  animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Email body preview - matches EXACTLY what's sent (white background, no border) */
.email-body-preview {
  background: white;
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #333;
  font-size: 14px;
  padding: 0;
  margin: 0;
}

/* Paragraph spacing matches email layout */
.email-body-preview :deep(p) {
  padding: 0;
  margin: 0 0 16px 0;
}

.email-body-preview :deep(p:last-child) {
  margin-bottom: 0;
}

/* List styling */
.email-body-preview :deep(ul),
.email-body-preview :deep(ol) {
  margin: 12px 0;
  padding-left: 24px;
}

.email-body-preview :deep(li) {
  margin: 4px 0;
}

/* Button styling - WHITE TEXT, NO UNDERLINE, NO HOVER CHANGE */
.email-body-preview :deep(table a) {
  color: #ffffff !important;
  text-decoration: none !important;
  font-weight: 600 !important;
}

.email-body-preview :deep(table a:hover) {
  color: #ffffff !important;
  text-decoration: none !important;
}

/* Link styling (not in buttons) */
.email-body-preview :deep(a:not(table a)) {
  color: #17B7B7;
  text-decoration: none;
  font-weight: 500;
}

.email-body-preview :deep(table) {
  margin: 20px 0;
}

/* Tailwind custom color for primary */
.bg-primary-color {
  background-color: #17B7B7;
}

.text-primary-color {
  color: #17B7B7;
}

.hover\:bg-teal-600:hover {
  background-color: #0fa8a8;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .grid-cols-2 {
    grid-template-columns: 1fr;
  }
}
</style>
